import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Check, X } from "lucide-react";
import { BIRTHDAY_CATEGORIES, phrasesFor, cardsFor, cardUrl } from "@/lib/birthdayKit";

// Composeur de vœux : « Pour qui ? » (catégorie) → phrases de la catégorie (un tap = texte dans le champ, modifiable)
// → carte facultative (un tap = sélection) → un seul bouton envoie texte + carte.
export const BirthdayComposer = ({ person, firstName, busy, onSend, t }) => {
  const [cat, setCat] = useState("friend");
  const [text, setText] = useState("");
  const [card, setCard] = useState(null);           // index dans BIRTHDAY_CARD_DEFS (src)
  const phrases = useMemo(() => phrasesFor(cat, t, firstName), [cat, t, firstName]);
  const cards = useMemo(() => cardsFor(cat), [cat]);
  const canSend = !busy && (!!text.trim() || !!card);
  const send = () => {
    if (!canSend) return;
    const content = text.trim() || (card ? t("birthdays.card_default", "Joyeux anniversaire ! 🎂") : "");
    onSend(content, card ? cardUrl(card) : null, card);
  };

  return (
    <div className="mt-3 space-y-2.5" data-testid={`bday-composer-${person.id}`} data-category={cat}>
      <div className="flex flex-wrap gap-1.5" data-testid={`bday-cats-${person.id}`}>
        <span className="text-[11px] font-semibold uppercase tracking-wide self-center mr-1" style={{ color: "var(--yalola-text-muted)" }}>{t("birthdays.for_whom", "Pour qui ?")}</span>
        {BIRTHDAY_CATEGORIES.map((c) => (
          <button key={c.key} type="button" onClick={() => setCat(c.key)} className={`chip text-xs ${cat === c.key ? "chip-active" : ""}`} style={cat === c.key ? { background: "var(--yalola-primary)", color: "#fff", borderColor: "var(--yalola-primary)" } : undefined} aria-pressed={cat === c.key} data-testid={`bday-cat-${person.id}-${c.key}`}>
            {t(c.labelKey, c.label)}
          </button>
        ))}
      </div>

      <div className="flex gap-2">
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          placeholder={t("birthdays.wish_placeholder", "Joyeux anniversaire, {name} !").replace("{name}", firstName)}
          className="rounded-full"
          data-testid={`bday-input-${person.id}`}
        />
        <Button size="icon" className="pill-primary rounded-full shrink-0" disabled={!canSend} onClick={send} aria-label={t("birthdays.send_wish", "Envoyer les vœux")} data-testid={`bday-send-${person.id}`}>
          <Send size={15} />
        </Button>
      </div>

      <div className="flex flex-wrap gap-1.5" data-testid={`bday-phrases-${person.id}`}>
        {phrases.map((w, i) => {
          const active = text.trim() === w;
          return (
            <button key={`${cat}-${i}`} type="button" disabled={busy} onClick={() => setText(active ? "" : w)} className="chip text-xs text-left hover:opacity-80 max-w-full whitespace-normal" style={active ? { borderColor: "var(--yalola-primary)", background: "var(--yalola-primary-soft)" } : undefined} aria-pressed={active} data-testid={`bday-quick-${person.id}-${i}`}>
              {w}
            </button>
          );
        })}
      </div>

      <div>
        <div className="text-[11px] font-semibold uppercase tracking-wide mb-1.5 flex items-center gap-2" style={{ color: "var(--yalola-text-muted)" }}>
          {t("birthdays.pick_card", "Ajouter une carte (facultatif)")}
          {card && (
            <button type="button" onClick={() => setCard(null)} className="inline-flex items-center gap-0.5 normal-case tracking-normal font-medium" style={{ color: "var(--yalola-primary)" }} data-testid={`bday-card-clear-${person.id}`}>
              <X size={11} /> {t("birthdays.card_remove", "Retirer la carte")}
            </button>
          )}
        </div>
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid={`bday-cards-${person.id}`}>
          {cards.map((c) => {
            const picked = card === c.src;
            const n = c.src.match(/birthday-(\d+)/)?.[1];
            return (
              <button
                key={c.src}
                type="button"
                disabled={busy}
                onClick={() => setCard(picked ? null : c.src)}
                className={`relative shrink-0 rounded-xl overflow-hidden border-2 transition-all duration-200 ${picked ? "scale-105" : "border-transparent hover:border-[color:var(--yalola-primary)] hover:scale-[1.03]"}`}
                style={picked ? { borderColor: "var(--yalola-primary)" } : undefined}
                aria-pressed={picked}
                data-testid={`bday-card-${person.id}-${Number(n) - 1}`}
                data-picked={picked ? "1" : "0"}
                data-featured={c.cats.includes(cat) ? "1" : "0"}
              >
                <img src={cardUrl(c.src)} alt={`Carte ${n}`} loading="lazy" className="h-16 w-16 object-cover" />
                {picked && (
                  <span className="absolute inset-0 grid place-items-center" style={{ background: "rgba(217,119,87,0.45)" }}>
                    <span className="h-7 w-7 rounded-full grid place-items-center bg-white bday-card-pop"><Check size={16} style={{ color: "var(--yalola-primary)" }} /></span>
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {(text.trim() || card) && (
        <Button className="pill-primary rounded-full w-full" disabled={!canSend} onClick={send} data-testid={`bday-send-combo-${person.id}`}>
          <Send size={14} className="mr-1.5" />
          {card && text.trim() ? t("birthdays.send_both", "Envoyer la carte et le message") : card ? t("birthdays.send_card_only", "Envoyer la carte") : t("birthdays.send_wish", "Envoyer les vœux")}
        </Button>
      )}
    </div>
  );
};
